import { useEffect, useState, type ReactNode } from "react";
import { BrowserRouter, matchPath, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@legends/ui";
import {
  adoptImpersonationHash,
  clearImpersonationToken,
  endImpersonation as endImpersonationApi,
  fetchAssignedEvents,
  fetchAuthStatus,
  fetchAuthz,
  fetchCaveForHost,
  fetchCaves,
  fetchConfigurableCaves,
  impersonationExpiry,
  type AssignedEvent,
  type AuthStatus,
  type AuthUser,
  type AuthzMe,
  type Cave,
  type TenantRole,
} from "./api";
import LoginScreen from "./LoginScreen";
import SignUpScreen from "./SignUpScreen";
import VerifyEmailScreen from "./VerifyEmailScreen";
import ForgotPasswordScreen from "./ForgotPasswordScreen";
import ResetPasswordScreen from "./ResetPasswordScreen";
import HomeScreen from "./HomeScreen";
import CavePublicScreen from "./CavePublicScreen";
import SelectorScreen from "./SelectorScreen";
import LegendProfileScreen from "./LegendProfileScreen";
import type { LegendLite } from "./api";
import CaveConsoleScreen from "./CaveConsoleScreen";
import AssignedEventsScreen from "./AssignedEventsScreen";
import ConsumerScreen from "./ConsumerScreen";
import LegendScreen from "./LegendScreen";
import DirectorScreen from "./DirectorScreen";
import GuestJoinScreen from "./GuestJoinScreen";
import WhiteboardDeviceScreen from "./WhiteboardDeviceScreen";
import ConsumerWatch from "./ConsumerWatch";
import LobbyPreviewScreen from "./LobbyPreviewScreen";
import LobbyDevicePreviewScreen from "./LobbyDevicePreviewScreen";
import { useGaPageViews } from "./useGaPageViews";

// Thin full-width strip shown in an impersonation tab: who/what the tab acts
// as, who the operator really is, a live countdown to the session's expiry, and
// the way out. Ending records the audit trail then drops the tab's token and
// closes; the operator's cookie session is untouched (ADR 0007/0160). The strip
// is warm amber (never mistaken for the operator's own session) and its counter
// turns urgent-red under a minute.
function ImpersonationBanner({ label, onEnd }: { label: string; onEnd: () => void }) {
  const [remainingMs, setRemainingMs] = useState<number | null>(() => {
    const exp = impersonationExpiry();
    return exp === null ? null : exp - Date.now();
  });
  useEffect(() => {
    const exp = impersonationExpiry();
    if (exp === null) return;
    const tick = () => setRemainingMs(exp - Date.now());
    tick();
    const h = window.setInterval(tick, 1000);
    return () => window.clearInterval(h);
  }, []);
  // Auto-return once the session has actually expired (the token stops working;
  // don't leave a dead tab sitting on the persona view).
  useEffect(() => {
    if (remainingMs !== null && remainingMs <= 0) onEnd();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remainingMs !== null && remainingMs <= 0]);

  const countdown =
    remainingMs === null
      ? null
      : (() => {
          const s = Math.max(0, Math.floor(remainingMs / 1000));
          return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
        })();
  const urgent = remainingMs !== null && remainingMs <= 60_000;

  return (
    <div
      role="status"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "5px 16px",
        background: "var(--alert-tint)",
        borderBottom: "1px solid var(--alert-edge)",
      }}
    >
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: "var(--fs-2xs)",
          letterSpacing: "var(--ls-label)",
          textTransform: "uppercase",
          color: "var(--alert-2)",
        }}
      >
        {label}
      </span>
      {countdown && (
        <span
          aria-label={`Impersonation session ends in ${countdown}`}
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "var(--fs-2xs)",
            fontWeight: 700,
            padding: "1px 8px",
            borderRadius: 999,
            color: urgent ? "#fff" : "var(--alert-2)",
            background: urgent ? "var(--live, #f0644a)" : "transparent",
            border: `1px solid ${urgent ? "transparent" : "var(--alert-edge)"}`,
          }}
        >
          ⧗ {countdown}
        </span>
      )}
      <Button size="sm" onClick={onEnd} style={{ marginLeft: "auto" }}>
        Return
      </Button>
    </div>
  );
}

function Splash({ text }: { text: string }) {
  return (
    <main
      className="mer-canvas mer-canvas--photo"
      style={{ minHeight: "calc(100vh / var(--ui-zoom, 1))", display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <p style={{ color: "var(--text-dim)", fontSize: "var(--fs-body)" }}>{text}</p>
    </main>
  );
}

// The host + auth + role router (ADR 0007). One bundle serves every surface:
// the cave storefronts (host-header match), the catalog (any other host), the
// persona views, and the admin portal — chosen at runtime from
// /public/cave?host, /auth/status and /authz/me.
// URL-driven routing (ADR 0021): the app lives at real paths so refresh,
// back/forward and deep links are stable. BrowserRouter wraps the whole tree so
// every screen can navigate; App holds the boot + persona logic.
export default function App() {
  return (
    <BrowserRouter>
      <AppInner />
    </BrowserRouter>
  );
}

function AppInner() {
  const navigate = useNavigate();
  const location = useLocation();
  // GA4 SPA page views (ADR 0224) — one per route (initial + client nav), works
  // on cave subdomain hosts too. A no-op unless window.__RUM__.ga4Id is set.
  useGaPageViews();
  // The entered legend id is the URL, not React state, so refresh keeps you on
  // the same legend (/legends/:id).
  const enteredId = matchPath("/legends/:id", location.pathname)?.params.id ?? null;
  // The public guest-publisher surface (ADR 0044) lives at /guest/:code and is
  // NOT behind AuthGate — a guest has no account. It's resolved before any
  // auth/persona boot so a guest never touches the operator surfaces.
  const guestCode = matchPath("/guest/:code", location.pathname)?.params.code ?? null;
  // The public Whiteboard-from-Device surface (ADR 0095) at /whiteboard/:code —
  // also NOT behind AuthGate; the touch device holds only the one-time code.
  const whiteboardCode = matchPath("/whiteboard/:code", location.pathname)?.params.code ?? null;
  // The headless program-renderer (LSC-429) at /program-render/:code — what the LiveKit
  // composite egress loads (CustomBaseURL) so HLS/VOD show the produced program. NOT
  // behind AuthGate; the renderer holds only the code (subscribe-only, no account).
  const renderCode = matchPath("/program-render/:code", location.pathname)?.params.code ?? null;
  // The same-browser lobby preview window (ADR 0134) at /caves/:caveId/lobby/preview
  // — renders purely from the editor's BroadcastChannel, so it needs no auth boot.
  const lobbyPreviewCaveId = matchPath("/caves/:caveId/lobby/preview", location.pathname)?.params.caveId ?? null;
  // The cross-device lobby preview (ADR 0134) at the PUBLIC /lobby-preview/:code —
  // scanned from the editor's QR; the device holds only the code (no account).
  const lobbyDeviceCode = matchPath("/lobby-preview/:code", location.pathname)?.params.code ?? null;
  // The email-verification landing page (LSC-554 / ADR 0233) at /verify-email?token=…
  // — where the emailed link lands; resolved before AuthGate (no session yet). The
  // one-time token rides the query string.
  const verifyEmailToken =
    location.pathname === "/verify-email" ? new URLSearchParams(location.search).get("token") ?? "" : null;
  // The password-reset landing page (LSC-614) at /reset-password?token=… — where the
  // emailed reset link lands; resolved before AuthGate (no session yet). The one-time
  // token rides the query string, exactly like /verify-email.
  const resetPasswordToken =
    location.pathname === "/reset-password" ? new URLSearchParams(location.search).get("token") ?? "" : null;

  // Adopt a /#imp=<token> launch before anything fetches, so every API call
  // in this tab (including the boot ones) carries the impersonation header.
  useState(() => {
    adoptImpersonationHash();
    return null;
  });

  const [status, setStatus] = useState<AuthStatus | null>(null);
  const [cave, setCave] = useState<Cave | null | undefined>(undefined);
  const [authz, setAuthz] = useState<AuthzMe | null>(null);
  // Caves the (possibly impersonated) identity may configure — drives the
  // Cave console. undefined = not yet resolved (ADR 0008).
  const [configCaves, setConfigCaves] = useState<Cave[] | undefined>(undefined);
  // CaveCasts the identity may produce via an event-scoped grant (ADR 0065) —
  // the entry point for an event-only director with no configurable caves.
  // undefined = not yet resolved.
  const [assignedEvents, setAssignedEvents] = useState<AssignedEvent[] | undefined>(undefined);
  // The impersonated tenant's cave, for view-impersonating "public".
  const [impCave, setImpCave] = useState<Cave | null | undefined>(undefined);
  const [showLogin, setShowLogin] = useState(
    () => new URLSearchParams(window.location.search).has("sso_error"),
  );
  // Which auth surface is showing when showLogin is set (LSC-554): the sign-in
  // console (local + SSO) or the local sign-up page.
  const [authView, setAuthView] = useState<"login" | "signup" | "forgot">("login");
  // The exact ticket a Fan was about to buy when bounced to sign in (ADR 0103).
  // Threaded into LoginScreen → the SSO start → the signed state, so the purchase
  // resumes on return. null = a plain sign-in with no pending purchase.
  const [pendingBuy, setPendingBuy] = useState<string | null>(null);
  const signIn = (buyTicketTypeId?: string) => {
    setPendingBuy(buyTicketTypeId ?? null);
    setShowLogin(true);
  };
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (guestCode || lobbyPreviewCaveId || lobbyDeviceCode) return; // these surfaces need no host/auth boot
    fetchCaveForHost(window.location.hostname)
      .then(setCave)
      .catch(() => setCave(null));
    fetchAuthStatus()
      .then((s) => {
        setStatus(s);
        if (s.state === "authenticated" && s.user) {
          fetchAuthz()
            .then(setAuthz)
            .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)));
        }
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)));
  }, [guestCode, lobbyPreviewCaveId, lobbyDeviceCode]);

  const viewImp = status?.state === "authenticated" ? status.viewImpersonation : undefined;
  const authedUsername = status?.state === "authenticated" ? status.user?.username : undefined;

  // Which caves may this identity configure? Run for every authenticated
  // identity (the impersonation token, if any, scopes it) so the console
  // catches legends, tenant admins and per-cave-granted directors alike.
  useEffect(() => {
    if (!authedUsername) {
      setConfigCaves(undefined);
      setAssignedEvents(undefined);
      return;
    }
    fetchConfigurableCaves()
      .then(setConfigCaves)
      .catch(() => setConfigCaves([]));
    // Event-scoped assignments (ADR 0065) — resolved alongside configurable caves
    // so an event-only director gets a real entry point. A stale/unsupported read
    // degrades to none rather than blocking the shell.
    fetchAssignedEvents()
      .then(setAssignedEvents)
      .catch(() => setAssignedEvents([]));
  }, [authedUsername]);

  useEffect(() => {
    if (viewImp?.view === "public") {
      const tenant = viewImp.tenant;
      fetchCaves()
        .then((list) => setImpCave(list.find((c) => c.tenant === tenant) ?? null))
        .catch(() => setImpCave(null));
    }
  }, [viewImp?.view, viewImp?.tenant]);

  const onLogin = (user: AuthUser) => {
    setShowLogin(false);
    setStatus((prev) => ({
      state: "authenticated",
      user,
      providers: prev?.providers ?? [],
    }));
    fetchAuthz()
      .then(setAuthz)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)));
  };

  const onLogout = () => {
    setAuthz(null);
    setShowLogin(false);
    setStatus((prev) => ({ state: "unauthenticated", providers: prev?.providers ?? [] }));
  };

  const endImpersonation = () => {
    // Record the end in the audit log while the token is still present (the
    // header carries it), then drop it. Best-effort — never trap the operator.
    void endImpersonationApi().finally(() => {
      clearImpersonationToken();
      window.close();
      // Close is often blocked for tabs the script didn't open itself — fall
      // back to the operator's own view (the cookie session is intact).
      if (!window.closed) {
        window.location.assign("/");
      }
    });
  };

  // The public guest-publisher surface (ADR 0044) — resolved before auth boot,
  // so a guest never sees the operator/persona routing or an API-boot error.
  if (guestCode) {
    return <GuestJoinScreen code={guestCode} />;
  }
  // The Whiteboard-from-Device page (ADR 0095) — resolved before auth boot too.
  if (whiteboardCode) {
    return <WhiteboardDeviceScreen code={whiteboardCode} />;
  }
  // The headless program-renderer (LSC-429) — resolved before auth boot; the LiveKit
  // composite egress loads it to capture the produced program for HLS/VOD.
  if (renderCode) {
    return <ConsumerWatch broadcastId="" renderCode={renderCode} />;
  }
  // The same-browser lobby preview window (ADR 0134) — pure BroadcastChannel
  // renderer, resolved before auth boot (the editor tab does the fetching).
  if (lobbyPreviewCaveId) {
    const mode = new URLSearchParams(location.search).get("mode") === "phone" ? "phone" : "desktop";
    return <LobbyPreviewScreen caveId={lobbyPreviewCaveId} mode={mode} />;
  }
  // The cross-device lobby preview page (ADR 0134) — public, code-authed.
  if (lobbyDeviceCode) {
    return <LobbyDevicePreviewScreen code={lobbyDeviceCode} />;
  }
  // The email-verification landing page (LSC-554) — confirms the token, signs the
  // fan in (onLogin adopts the session), then drops them home.
  if (verifyEmailToken !== null) {
    return (
      <VerifyEmailScreen
        token={verifyEmailToken}
        onVerified={(user) => {
          onLogin(user);
          navigate("/");
        }}
        onBackToLogin={() => {
          navigate("/");
          setShowLogin(true);
        }}
      />
    );
  }
  // The password-reset landing page (LSC-614) — sets the new password, signs the fan in
  // (onReset adopts the fresh session), then drops them home. Resolved before AuthGate.
  if (resetPasswordToken !== null) {
    return (
      <ResetPasswordScreen
        token={resetPasswordToken}
        onReset={(user) => {
          onLogin(user);
          navigate("/");
        }}
        onBackToLogin={() => {
          navigate("/");
          setShowLogin(true);
        }}
      />
    );
  }

  if (error) {
    return (
      <main
        className="mer-canvas mer-canvas--photo"
        style={{ minHeight: "calc(100vh / var(--ui-zoom, 1))", display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <p role="alert" style={{ color: "var(--alert-2)", fontSize: "var(--fs-body)" }}>
          API unreachable: {error}
        </p>
      </main>
    );
  }

  // The apex (non-cave-host) experience is URL-driven (ADR 0021): /legends/:id
  // is a legend's Cave/profile (deep-linkable, refresh-stable); anything else is
  // the Selector funnel. Choosing a legend navigates; Back / the logo go home.
  const enterLegend = (l: LegendLite) => navigate(`/legends/${l.id}`);
  const goHome = () => navigate("/");
  const renderApex = (opts: { onSignIn?: () => void; onLogout?: () => void }) => {
    if (enteredId) {
      return (
        <LegendProfileScreen
          legendId={enteredId}
          onBack={goHome}
          onSignIn={opts.onSignIn}
          onLogout={opts.onLogout}
        />
      );
    }
    return <SelectorScreen onEnter={enterLegend} onSignIn={opts.onSignIn} onLogout={opts.onLogout} />;
  };

  if (!status || cave === undefined) {
    return <Splash text="Establishing link…" />;
  }

  if (status.state !== "authenticated" || !status.user) {
    if (showLogin) {
      // Local sign-up page (LSC-554) — reached via "Create an account" on the login.
      if (authView === "signup") {
        return <SignUpScreen onBackToLogin={() => setAuthView("login")} onLogin={onLogin} />;
      }
      // Forgot-password request page (LSC-614) — reached via "Forgot password?" on login.
      if (authView === "forgot") {
        return <ForgotPasswordScreen onBackToLogin={() => setAuthView("login")} />;
      }
      // A failed SSO round-trip bounces back with ?sso_error=… — surface it once.
      const ssoError = new URLSearchParams(window.location.search).get("sso_error") ?? undefined;
      return (
        <LoginScreen
          providers={status.providers}
          onLogin={onLogin}
          ssoError={ssoError}
          buyTicketTypeId={pendingBuy ?? undefined}
          onCreateAccount={() => setAuthView("signup")}
          onForgotPassword={() => setAuthView("forgot")}
        />
      );
    }
    return cave ? (
      <CavePublicScreen name={cave.name} caveId={cave.id} onSignIn={signIn} />
    ) : (
      renderApex({ onSignIn: signIn })
    );
  }

  if (!authz) {
    return <Splash text="Establishing link…" />;
  }

  const user = status.user;

  // View impersonation outranks everything: render that persona's view for
  // the tenant, no target user involved.
  if (viewImp) {
    const banner = (
      <ImpersonationBanner
        label={`Viewing ${viewImp.view} · ${viewImp.tenant} · Operator ${
          status.impersonator ?? user.username
        }`}
        onEnd={endImpersonation}
      />
    );
    // The persona/public screens keep the banner as a strip above them; the
    // Cave console owns its chrome, so the banner rides inside its shell slot.
    const withBanner = (node: ReactNode): ReactNode => (
      <>
        {banner}
        {node}
      </>
    );
    // A configurable-caves identity gets the Cave console in place of the
    // legend/director placeholder (ADR 0008).
    const consoleIfCaves = (fallback: ReactNode): ReactNode => {
      if (configCaves === undefined) return withBanner(<Splash text="Establishing link…" />);
      return configCaves.length > 0 ? (
        <CaveConsoleScreen caves={configCaves} user={user} authz={authz} onLogout={onLogout} banner={banner} />
      ) : (
        withBanner(fallback)
      );
    };
    if (viewImp.view === "public") {
      return withBanner(
        impCave === undefined ? (
          <Splash text="Establishing link…" />
        ) : (
          <CavePublicScreen name={impCave?.name ?? viewImp.tenant} caveId={impCave?.id} />
        ),
      );
    }
    if (viewImp.view === "consumer") return withBanner(<ConsumerScreen onLogout={onLogout} />);
    if (viewImp.view === "director") return consoleIfCaves(<DirectorScreen onLogout={onLogout} />);
    return consoleIfCaves(<LegendScreen onLogout={onLogout} />);
  }

  // Role resolution — for the impersonated target when the tab carries a user
  // impersonation token (authz already reflects the target), else the caller.
  const hasRole = (role: TenantRole) => authz.tenants.some((t) => t.roles.includes(role));
  // The operator impersonation strip, if any — passed into the shell screens
  // (HomeScreen/CaveConsole) via their banner slot, rendered above the others.
  const banner = status.impersonator ? (
    <ImpersonationBanner
      label={`Impersonating ${user.username} · Operator ${status.impersonator}`}
      onEnd={endImpersonation}
    />
  ) : null;
  const withBanner = (node: ReactNode): ReactNode => (
    <>
      {banner}
      {node}
    </>
  );

  // A legend deep-link (/legends/:id) works for any signed-in user, above the
  // persona home, so it survives refresh and is shareable (ADR 0021).
  if (enteredId && !cave) {
    return withBanner(renderApex({ onLogout }));
  }

  if (authz.superAdmin) {
    return <HomeScreen user={user} authz={authz} onLogout={onLogout} banner={banner} />;
  }
  if (configCaves === undefined || assignedEvents === undefined) {
    // Still resolving which caves this identity may configure + which events it is
    // assigned to produce (ADR 0065).
    return withBanner(<Splash text="Establishing link…" />);
  }

  // On a CAVE host, the operator console is only for an operator of THIS cave.
  // Everyone else — a plain fan, OR an operator of a *different* cave — gets this
  // cave's public/fan view. (Previously any identity with any configurable cave
  // got the console on every cave host, so operators never saw the fan view and a
  // mis-granted "fan" looked like a legend/director — ADR 0028.)
  if (cave) {
    if (configCaves.some((c) => c.id === cave.id)) {
      return (
        <CaveConsoleScreen
          caves={configCaves}
          user={user}
          authz={authz}
          onLogout={onLogout}
          banner={banner}
          assignedEvents={assignedEvents}
        />
      );
    }
    // Not a configurator of THIS cave, but assigned to produce one of its events
    // (ADR 0065): the event-only entry point, not the fan storefront.
    const forThisCave = assignedEvents.filter((e) => e.caveId === cave.id);
    if (forThisCave.length > 0) {
      return (
        <AssignedEventsScreen events={forThisCave} user={user} onLogout={onLogout} banner={banner} />
      );
    }
    return withBanner(<CavePublicScreen name={cave.name} caveId={cave.id} onLogout={onLogout} vip={authz.vip ?? false} />);
  }

  // Apex (no cave host): an operator manages from the Cave console; a bare
  // legend/director role without a cave lands on its persona placeholder; a fan
  // gets the storefront. No dead-end — a signed-in fan can always shop.
  if (configCaves.length > 0) {
    return (
      <CaveConsoleScreen
        caves={configCaves}
        user={user}
        authz={authz}
        onLogout={onLogout}
        banner={banner}
        assignedEvents={assignedEvents}
      />
    );
  }
  // An event-only director/legend (event_config, no configurable caves — ADR
  // 0065) lands on their assigned-events entry, above the persona placeholder.
  if (assignedEvents.length > 0) {
    return <AssignedEventsScreen events={assignedEvents} user={user} onLogout={onLogout} banner={banner} />;
  }
  if (hasRole("director")) return withBanner(<DirectorScreen onLogout={onLogout} />);
  if (hasRole("legend")) return withBanner(<LegendScreen onLogout={onLogout} />);
  return withBanner(renderApex({ onLogout }));
}
